/* eslint no-underscore-dangle: 0 */

import 'react-virtualized/styles.css';
import React, { ChangeEventHandler, Component, MouseEventHandler } from 'react';

import {
  AutoSizer,
  Column,
  SortDirection,
  SortDirectionType,
  SortIndicator,
  Table,
  TableHeaderRenderer,
  defaultTableHeaderRenderer,
} from 'react-virtualized';
import {
  BuildJsonType,
  EnhancedAppointment,
  JsonObject,
} from '@bb/domain/dist/types';
import PropTypes from 'prop-types';

import { memoize } from 'lodash/fp';
import { orderBy } from 'lodash';

import {
  formatTimeStr,
  formatDateStr,
  formatDateStrWithYearShort,
} from 'utils';
import { Empty } from 'components';
import styled from 'styled-components';
import { ListingDoc } from '@bb/domain/dist/schemas';
import { APPOINTMENT_STATUS_FOR_COVID_19_LABEL } from '../../constants';

type AppointmentData = {
  listing: JsonObject<ListingDoc>;
  appointment: BuildJsonType<EnhancedAppointment>;
};

/** If local filter, filters client side. */
type FilterMode = 'LOCAL_FILTER' | 'QUERY_FILTER';

/**
 * Filters:
 *
 * Groups can be created by filters with the signature (appointment: AppointmentData) => boolean.
 *
 * There is always only one active filter, but there can be many secondary filters that are combined
 * with the active filter.
 *
 * One such secondary filter is the LA exclude filter, and more can be injected by the
 * secondaryFilters prop.
 */
type GroupFilter = (appointment: AppointmentData) => boolean;

const FILTER_DEFAULT: GroupFilter = (_: AppointmentData) => true;
const FILTER_LA_EXCLUDE: GroupFilter = ({ appointment }: AppointmentData) => {
  if (!appointment.listingAgentConfirm) return true;
  return !appointment.listingAgentConfirm.excludeFromQueue;
};

const makeOrgFilter =
  (orgName: string): GroupFilter =>
  ({ appointment }: AppointmentData) =>
    appointment.coop.org.name === orgName;
const makeAddressFilter =
  (listingId: string): GroupFilter =>
  ({ appointment }: AppointmentData) =>
    appointment.listingId === listingId;
const combineFilters =
  (...filters: GroupFilter[]): GroupFilter =>
  (data: AppointmentData) =>
    filters.reduce((prev, filter) => prev && filter(data), true);

interface QueueListProps {
  data: { [id: string]: AppointmentData };
  selected: { [id: string]: boolean };
  viewing: { [id: string]: boolean };
  secondaryFilters?: {
    [filterBy: string]: GroupFilter;
  };
  filterMode: FilterMode;
  onAppointmentFlagged?: (
    appointment: AppointmentData['appointment'],
    newFlagState: boolean,
  ) => void;
  onAppointmentSelected?: (sortedData: AppointmentData[]) => void;
  onAppointmentView?: (appointment: AppointmentData['appointment']) => void;
  onFilteredCountChanged?: (newCount: number, filter: GroupFilter) => void;
  onFilterQuery?: (query: { [id: string]: string }) => void;
  isBeingViewed?: (appointmentId: string) => boolean;
  onSort?: (sortedData: AppointmentData[]) => void;
}

interface QueueListState {
  sortedData: AppointmentData[];
  sortBy: string;
  sortDirection: SortDirectionType;
  orgGroups: { [orgName: string]: number };
  addressGroups: { [addressName: string]: number };
  activeFilter: GroupFilter;
  secondaryFilters: {
    [filterBy: string]: GroupFilter;
  };
  filterBy: string;
  showingHiddenLAToConfirm: boolean;
}

type PropTypes = { [prop: string]: PropTypes.Validator<unknown> };

const DateColumnRenderer = ({ rowData }: { rowData: AppointmentData }) => {
  const { appointment } = rowData;
  return (
    <div style={{ color: '#000000' }}>
      <div>{formatDateStr(appointment.time, appointment.listing.timezone)}</div>
      <div>
        {formatTimeStr(appointment.time, appointment.listing.timezone)} to{' '}
        {formatTimeStr(appointment.timeEnd, appointment.listing.timezone)}
      </div>
    </div>
  );
};

const StatusColumnRenderer = ({ rowData }: { rowData: AppointmentData }) => {
  const { appointment } = rowData;
  return (
    <div style={{ color: '#000000' }}>
      <StyledStatusText mustCall={appointment.legacyStatus.mustCall}>
        {appointment.legacyStatus.note}
      </StyledStatusText>
      {!appointment.legacyStatus.mustCall && appointment.mustCall && (
        <StyledStatusText mustCall>
          MUST CALL LISTING CONTACT TO CONFIRM
        </StyledStatusText>
      )}
      <div>
        {`${formatDateStrWithYearShort(
          appointment.legacyStatus.time,
          appointment.listing.timezone,
        )} ${formatTimeStr(
          appointment.legacyStatus.time,
          appointment.listing.timezone,
        )}`}
      </div>

      {appointment.covid19RequestedAck &&
        APPOINTMENT_STATUS_FOR_COVID_19_LABEL.includes(appointment.status) && (
          <div>
            {!appointment.covid19Signed && (
              <strong className="covid19-notsigned">
                Waiting for COVID-19 Form
              </strong>
            )}
            {appointment.covid19Signed && !appointment.covid19Overridden && (
              <strong className="covid19-signed">COVID-19 Form Signed</strong>
            )}
            {appointment.covid19Overridden && (
              <strong className="covid19-signed">
                Covid-19 Form Overridden
              </strong>
            )}
          </div>
        )}

      {['new', 'attempted', 'time_change'].includes(appointment.status) &&
        appointment.multiConf && (
          <div>
            <small className="text-primary-2">
              <i className="pe-7s-attention fat"></i> MULTIPLE APPROVALS (
              {
                appointment.multiConf.filter(
                  contact => contact.status !== 'confirm',
                ).length
              }{' '}
              of {appointment.multiConf.length} Remaining)
            </small>
          </div>
        )}
      {appointment.listingAgentConfirm &&
      appointment.listingAgentConfirm.enabled ? (
        <strong className="hiding-la-confirm">LA to Confirm</strong>
      ) : null}
    </div>
  );
};

const StopEvent = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
  e.preventDefault();
  e.stopPropagation();
};

class QueueList extends Component<QueueListProps, QueueListState> {
  static propTypes: PropTypes;

  private tableRef: React.RefObject<Table>;

  static getDerivedStateFromProps(
    nextProps: QueueListProps,
    prevState: QueueListState,
  ): QueueListState {
    const { data, secondaryFilters } = nextProps;
    const dataArray = data ? Object.values(data) : [];
    const stateSecondaryFilters = {
      ...prevState.secondaryFilters,
      ...(secondaryFilters || {}),
    };
    const { orgGroups, addressGroups } = QueueList._buildGroups(dataArray);
    return {
      ...prevState,
      secondaryFilters: stateSecondaryFilters,
      sortedData: QueueList._sortList(
        dataArray,
        prevState.sortBy,
        prevState.sortDirection,
        combineFilters(
          prevState.activeFilter,
          ...Object.values(stateSecondaryFilters),
        ),
      ),
      orgGroups,
      addressGroups,
    };
  }

  static _buildGroups(
    list: AppointmentData[],
  ): Pick<QueueListState, 'orgGroups' | 'addressGroups'> {
    if (!list) return { orgGroups: {}, addressGroups: {} };

    const orgGroups: {
      [org: string]: number;
    } = {};

    const addressGroups: {
      [address: string]: number;
    } = {};

    list.forEach(({ appointment }) => {
      addressGroups[appointment.listingId] =
        (addressGroups[appointment.listingId] || 0) + 1;
      orgGroups[appointment.coop.org.name] =
        (orgGroups[appointment.coop.org.name] || 0) + 1;
    });

    return { orgGroups, addressGroups };
  }

  static _sortList(
    list: AppointmentData[],
    sortKey: string,
    sortDirection: SortDirectionType,
    filter: GroupFilter,
  ) {
    if (!list) return [];

    const sortedlist = orderBy(
      list.filter(filter),
      [
        a => a.appointment.urgency || 0,
        a => {
          switch (sortKey) {
            case 'address':
              return a.appointment.listing.line1;
            case 'agent':
              return a.appointment.coop.name;
            case 'status':
              return a.appointment.status;
            case 'date':
            default:
              return a.appointment.time;
          }
          // This behaviour perfectly replicates the sorting behaviour from
          // the legacy queue component, despite the sort directions not
          // matching up. When the arrow points down (SortDirection.DESC),
          // the order is ascending, so we match that here.
        },
      ],
      ['asc', sortDirection === SortDirection.ASC ? 'desc' : 'asc'],
    );
    return sortedlist;
  }

  constructor(props: QueueListProps) {
    super(props);
    this.state = {
      sortedData: [],
      sortBy: window.localStorage.getItem('sortBy') || 'date',
      sortDirection:
        (window.localStorage.getItem('sortDirection') as SortDirectionType) ||
        SortDirection.DESC,
      orgGroups: {},
      addressGroups: {},
      activeFilter: FILTER_DEFAULT,
      secondaryFilters: {
        listingAgentExclude: FILTER_LA_EXCLUDE,
      },
      filterBy: 'none',
      showingHiddenLAToConfirm: false,
    };
    this.tableRef = React.createRef();
  }

  FlaggedColumnRenderer = ({ rowData }: { rowData: AppointmentData }) => {
    const { selected } = this.props;
    const { appointment } = rowData;
    return (
      <div onClick={StopEvent}>
        <div>
          <i
            onClick={memoize(this._onAppointmentFlagged(rowData))}
            className={
              appointment.flagged
                ? 'pe-7s-attention fat attention attention-hover'
                : 'pe-7s-attention fat attention-hover'
            }
            style={{ fontSize: 16 }}
          />
        </div>
        <input
          onChange={memoize(this._onAppointmentSelected(rowData))}
          type="checkbox"
          checked={!!selected[rowData.appointment.id]}
        />
      </div>
    );
  };

  AddressColumnRenderer = ({ rowData }: { rowData: AppointmentData }) => {
    const { addressGroups, filterBy } = this.state;
    const { isBeingViewed, viewing, filterMode } = this.props;
    const { appointment } = rowData;
    return (
      <div className="queue-cell">
        {
          // Show a green icon based on two sources.
          (isBeingViewed && isBeingViewed(appointment.id)) ||
          viewing[appointment.id] ? (
            <>
              <i
                className="fa fa-users"
                aria-hidden="true"
                style={{ color: 'green' }}
              />
              &nbsp;
            </>
          ) : null
        }

        <a
          style={{ fontWeight: 'bold', whiteSpace: 'normal' }}
          data-cy={appointment.id}
        >
          {appointment.listing.line1} {appointment.listing.line2}
        </a>

        {addressGroups[rowData.appointment.listingId] > 1 &&
        filterBy !== 'address' &&
        filterMode === 'LOCAL_FILTER' ? (
          <div
            className="btn btn-primary btn-xs pull-right"
            style={{ zIndex: 10 }}
            onClick={this._handleSetFilter(
              makeAddressFilter(rowData.appointment.listingId),
              'address',
            )}
          >
            {addressGroups[rowData.appointment.listingId]}
          </div>
        ) : null}

        {filterMode === 'QUERY_FILTER' ? (
          <div
            className="btn btn-primary btn-xs pull-right queue-filter-button"
            style={{ zIndex: 10 }}
            onClick={this._handleQuery({
              listing: `listing:${appointment.listingId}`,
            })}
          >
            <i className="fa fa-search" aria-hidden="true"></i>
          </div>
        ) : null}
      </div>
    );
  };

  AgentColumnRenderer = ({ rowData }: { rowData: AppointmentData }) => {
    const { orgGroups, filterBy } = this.state;
    const { filterMode } = this.props;
    const { appointment } = rowData;
    return (
      <div className="queue-cell" style={{ color: '#000000' }}>
        <div>
          <div style={{ zIndex: 10 }}>
            <span>{appointment.coop.name}</span>
            {orgGroups[rowData.appointment.coop.org.name] > 1 &&
            filterBy !== 'agent' &&
            filterMode === 'LOCAL_FILTER' ? (
              <div
                className="btn btn-success btn-xs pull-right"
                style={{ zIndex: 10 }}
                onClick={this._handleSetFilter(
                  makeOrgFilter(rowData.appointment.coop.org.name),
                  'agent',
                )}
              >
                {orgGroups[rowData.appointment.coop.org.name]}
              </div>
            ) : null}

            {filterMode === 'QUERY_FILTER' ? (
              <div
                className="btn btn-success btn-xs pull-right queue-filter-button"
                style={{ zIndex: 10 }}
                onClick={this._handleQuery({ agent: appointment.coop.name })}
              >
                <i className="fa fa-search" aria-hidden="true"></i>
              </div>
            ) : null}
          </div>
          <div style={{ fontWeight: 'bold' }}>{appointment.coop.org.name}</div>
          <div
            style={{ fontSize: 11 }}
            className={
              [
                'Buyer Visit',
                'Appraisal',
                'Home Inspection',
                'Photographer',
                'Pest Control',
              ].includes(appointment.type)
                ? 'text-danger'
                : ''
            }
          >
            {appointment.type}
          </div>
        </div>
      </div>
    );
  };

  FilterableHeaderRowRenderer: TableHeaderRenderer = ({
    dataKey,
    label,
    sortBy,
    sortDirection,
  }) => {
    const { filterBy } = this.state;
    const showSortIndicator = sortBy === dataKey;
    const showClearFilter = filterBy === dataKey;
    const children = [
      <span
        className="ReactVirtualized__Table__headerTruncatedText"
        key="label"
        title={typeof label === 'string' ? label : null}
      >
        {label}
      </span>,
    ];

    if (showClearFilter) {
      children.push(
        <span
          className="ReactVirtualized__Table__headerTruncatedText pull-right"
          style={{
            color: 'red',
            textTransform: 'none',
            textDecoration: 'none',
          }}
          onClick={this.resetActiveFilter}
          key="clearFilter"
        >
          <i className="fa fa-times" /> Clear Filter
        </span>,
      );
    }

    if (showSortIndicator) {
      children.push(
        <SortIndicator key="SortIndicator" sortDirection={sortDirection} />,
      );
    }
    return children;
  };

  HideLAHeaderRowRenderer: TableHeaderRenderer = ({
    dataKey,
    label,
    sortBy,
    sortDirection,
  }) => {
    const { showingHiddenLAToConfirm } = this.state;
    const { data } = this.props;
    const showSortIndicator = sortBy === dataKey;
    const values = Object.values(data);
    const excludeCount =
      values.length - values.filter(FILTER_LA_EXCLUDE).length;
    const children = [
      <span
        className="ReactVirtualized__Table__headerTruncatedText"
        key="label"
        title={typeof label === 'string' ? label : null}
      >
        {label}
      </span>,
    ];

    if (excludeCount) {
      children.push(
        <span
          className="ReactVirtualized__Table__headerTruncatedText pull-right"
          style={{ textTransform: 'none', textDecoration: 'underline' }}
          onClick={this._handleToggleShowHiddenLA}
          key="clearFilterLAExclude"
        >
          <span
            style={{ color: showingHiddenLAToConfirm ? 'darkgreen' : 'red' }}
          >
            {showingHiddenLAToConfirm ? (
              <>
                <i className="fa fa-times" />
                &nbsp;
              </>
            ) : null}
            {showingHiddenLAToConfirm ? 'Showing' : 'Hiding'} ({excludeCount}
            ){' '}
          </span>
          <span>{`LA to Confirm`}</span>
        </span>,
      );
    }

    if (showSortIndicator) {
      children.push(
        <SortIndicator key="SortIndicator" sortDirection={sortDirection} />,
      );
    }

    return children;
  };

  applyActiveFilter = (filter: GroupFilter, filterBy: string) => {
    this.setState({ filterBy, activeFilter: filter });
  };

  resetActiveFilter = () => {
    this.applyActiveFilter(FILTER_DEFAULT, 'none');
  };

  applySecondaryFilter = (filter: GroupFilter, filterBy: string) => {
    const { secondaryFilters } = this.state;
    this.setState({
      secondaryFilters: { ...secondaryFilters, [filterBy]: filter },
    });
  };

  removeSecondaryFilter = (filter: GroupFilter, filterBy: string) => {
    const { secondaryFilters } = this.state;
    const { [filterBy]: poppedFilter, ...rest } = secondaryFilters;
    this.setState({ secondaryFilters: rest });
  };

  _handleSetFilter =
    (filter: GroupFilter, filterBy: string): MouseEventHandler =>
    e => {
      e.preventDefault();
      e.stopPropagation();

      this.applyActiveFilter(filter, filterBy);

      this.tableRef.current.scrollToPosition(0);
    };

  _handleQuery =
    (query: { [id: string]: string }): MouseEventHandler =>
    e => {
      const { onFilterQuery } = this.props;
      e.preventDefault();
      e.stopPropagation();
      if (onFilterQuery) onFilterQuery(query);
    };

  _handleToggleShowHiddenLA: MouseEventHandler = e => {
    const { showingHiddenLAToConfirm } = this.state;
    e.preventDefault();
    e.stopPropagation();

    if (!showingHiddenLAToConfirm) {
      this.removeSecondaryFilter(FILTER_LA_EXCLUDE, 'listingAgentExclude');
      this.setState({ showingHiddenLAToConfirm: true });
    } else {
      this.applySecondaryFilter(FILTER_LA_EXCLUDE, 'listingAgentExclude');
      this.setState({ showingHiddenLAToConfirm: false });
    }
  };

  _onAppointmentFlagged =
    (data: AppointmentData): MouseEventHandler =>
    e => {
      const { onAppointmentFlagged } = this.props;
      e.stopPropagation();
      e.preventDefault();
      if (!onAppointmentFlagged) return;
      onAppointmentFlagged(data.appointment, !data.appointment.flagged);
    };

  _onAppointmentSelected =
    (data: AppointmentData): ChangeEventHandler =>
    e => {
      const { onAppointmentSelected } = this.props;
      if (!onAppointmentSelected) return;
      onAppointmentSelected(this._getSelectedAppointments(data.appointment.id));
    };

  _sort = ({
    sortBy,
    sortDirection,
  }: {
    sortBy: string;
    sortDirection: SortDirectionType;
  }) => {
    const { activeFilter, sortedData, secondaryFilters } = this.state;
    const { onSort } = this.props;
    const _sortedData = QueueList._sortList(
      sortedData,
      sortBy,
      sortDirection,
      combineFilters(activeFilter, ...Object.values(secondaryFilters)),
    );
    this.setState({ sortBy, sortDirection, sortedData: _sortedData }, () =>
      onSort(this._getSelectedAppointments()),
    );

    // Persist sort settings locally
    window.localStorage.setItem('sortBy', sortBy);
    window.localStorage.setItem('sortDirection', sortDirection);
  };

  _getSelectedAppointments = (selectedAppointmentId?: string, sort = true) => {
    const { activeFilter, secondaryFilters, sortBy, sortDirection } =
      this.state;
    const { data, selected } = this.props;
    let selectedAppointments;
    const _selected = { ...selected };
    if (selectedAppointmentId)
      _selected[selectedAppointmentId] = !selected[selectedAppointmentId];
    selectedAppointments = Object.entries(_selected).reduce(
      (acc: AppointmentData[], [key, value]) => {
        if (value) acc.push(data[key]);
        return acc;
      },
      [],
    );
    if (sort)
      selectedAppointments = QueueList._sortList(
        selectedAppointments,
        sortBy,
        sortDirection,
        combineFilters(activeFilter, ...Object.values(secondaryFilters)),
      );
    return selectedAppointments;
  };

  componentDidUpdate(_prevProps: QueueListProps, prevState: QueueListState) {
    const { activeFilter, sortedData, secondaryFilters } = this.state;
    const { onFilteredCountChanged } = this.props;
    if (!onFilteredCountChanged) return;
    onFilteredCountChanged(
      sortedData.length,
      combineFilters(activeFilter, ...Object.values(secondaryFilters)),
    );
  }

  render() {
    const { sortedData, sortBy, sortDirection } = this.state;
    const { onAppointmentView, filterMode } = this.props;
    return (
      <div style={{ height: '100%' }}>
        <AutoSizer>
          {({ height, width }) => (
            <Table
              ref={this.tableRef}
              overscanRowCount={3}
              width={width}
              height={height}
              headerHeight={20}
              rowHeight={80}
              rowCount={(sortedData || []).length || 0}
              rowGetter={({ index }) => sortedData[index]}
              headerClassName={'queue-header'}
              rowClassName={({ index }) => {
                if (index < 0) return 'queue-header-row';
                const rowData = sortedData[index];
                let className = 'queue-row';
                if (
                  ['Home Inspection', 'Whole-Home Inspection'].includes(
                    rowData.appointment.type,
                  )
                ) {
                  className += ' queue-row-home-inspection';
                }

                if (rowData.appointment.type === 'Appraisal') {
                  className += ' queue-row-appraisal';
                }

                if (
                  rowData.appointment.legacyStatus.mustCall ||
                  rowData.appointment.mustCall
                ) {
                  className += ' queue-row-must-call';
                }

                if (typeof rowData.appointment.urgency === 'number') {
                  if (
                    rowData.appointment.urgency === 1 &&
                    !rowData.appointment.flagged
                  ) {
                    const calculateTimeThreshold = (
                      hours: number,
                      notice?: number,
                      noticeMinutes?: number,
                    ) => (
                        Date.now() +
                        hours * 60 * 60 * 1000 +
                        ((notice ?? 0) * 60 + (noticeMinutes ?? 0)) * 60 * 1000
                      );

                    const { notice, noticeMinutes, time } = rowData.appointment;
                    const appointmentTime = new Date(time).getTime();

                    if (
                      calculateTimeThreshold(1, notice, noticeMinutes) >=
                      appointmentTime
                    ) {
                      className += ` urgency-${rowData.appointment.urgency}`;
                    } else if (
                      calculateTimeThreshold(3, notice, noticeMinutes) >=
                      appointmentTime
                    ) {
                      className += ' queue-row-start-soon';
                    }
                  } else {
                    className += ` urgency-${rowData.appointment.urgency}`;
                  }
                }
                return className;
              }}
              sort={this._sort}
              sortDirection={sortDirection}
              sortBy={sortBy}
              onRowClick={({ index }) => {
                if (onAppointmentView) {
                  onAppointmentView(sortedData[index].appointment);
                }
              }}
              noRowsRenderer={() => (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            >
              <Column
                dataKey="flagged"
                cellRenderer={this.FlaggedColumnRenderer}
                width={25}
              />
              <Column
                label="Address"
                dataKey="address"
                headerRenderer={
                  filterMode === 'LOCAL_FILTER'
                    ? this.FilterableHeaderRowRenderer
                    : defaultTableHeaderRenderer
                }
                cellRenderer={this.AddressColumnRenderer}
                width={300}
              />
              <Column
                width={150}
                label="Date"
                dataKey="time"
                cellRenderer={DateColumnRenderer}
                cellDataGetter={({ rowData }: { rowData: AppointmentData }) =>
                  new Date(rowData.appointment.time)
                }
              />
              <Column
                width={400}
                label="Agent"
                dataKey="agent"
                headerRenderer={
                  filterMode === 'LOCAL_FILTER'
                    ? this.FilterableHeaderRowRenderer
                    : defaultTableHeaderRenderer
                }
                cellRenderer={this.AgentColumnRenderer}
              />
              <Column
                width={400}
                flexGrow={1}
                label="Status"
                dataKey="status"
                headerRenderer={this.HideLAHeaderRowRenderer}
                cellRenderer={StatusColumnRenderer}
                cellDataGetter={({ rowData }: { rowData: AppointmentData }) =>
                  rowData.appointment.legacyStatus.note
                }
              />
            </Table>
          )}
        </AutoSizer>
      </div>
    );
  }
}

QueueList.propTypes = {
  data: PropTypes.object.isRequired,
  selected: PropTypes.object.isRequired,
  viewing: PropTypes.object.isRequired,
  secondaryFilters: PropTypes.objectOf(PropTypes.func),
  onAppointmentFlagged: PropTypes.func,
  onAppointmentSelected: PropTypes.func,
  onAppointmentView: PropTypes.func,
  onFilteredCountChanged: PropTypes.func,
  onFilterQuery: PropTypes.func,
  onSort: PropTypes.func,
  isBeingViewed: PropTypes.func,
  filterMode: PropTypes.oneOf(['LOCAL_FILTER', 'QUERY_FILTER']),
};

export default QueueList;

const StyledStatusText = styled.div<{ mustCall?: boolean }>`
  color: ${props => (props.mustCall ? 'red' : 'inherit')};
  font-weight: ${props => (props.mustCall ? 'bold' : 'normal')};
`;
